/**
 * useMediaStreamCleanup — additive resilience layer for any booth
 * capture flow that uses `getUserMedia` + a <video> element.
 *
 * What this hook adds on top of a component's existing stopCamera:
 *
 *   • visibilitychange  — when the kiosk tab backgrounds (the
 *     venue's screen-saver kicks in, the operator alt-tabs to the
 *     pairing page, an iOS Safari moves to the home screen) the
 *     active MediaStream is stopped immediately. The next time the
 *     component re-enters a live stage it will reacquire the
 *     camera through its existing startCamera path. This prevents
 *     the camera LED from staying on indefinitely + frees the
 *     hardware so a parallel surface can use it.
 *
 *   • pagehide          — covers iOS Safari's bfcache transition
 *     where `visibilitychange` may not fire reliably.
 *
 *   • beforeunload      — final-chance cleanup on full-page reload
 *     so a stale stream never survives a refresh.
 *
 * What this hook does NOT do:
 *
 *   • It does NOT restart the camera. That's the caller's
 *     responsibility (their existing stage-driven effect already
 *     handles it).
 *   • It does NOT change copy, gestures, or timings.
 *   • It does NOT introduce any visible state.
 *
 * Constitutional contract: this is invisible resilience work —
 * "nothing looks different, but the booth survives the night."
 *
 * @param {React.RefObject<MediaStream|null>} streamRef
 * @param {React.RefObject<HTMLVideoElement|null>} videoRef
 *   Optional — when present, the cleanup nulls `videoRef.current.srcObject`
 *   after stopping tracks. This breaks the video element's hold on
 *   the stream and is the difference between "stopped" and
 *   "garbage-collectable" under long-running pressure.
 */
import { useEffect } from 'react';

export function useMediaStreamCleanup(streamRef, videoRef) {
  useEffect(() => {
    const stop = () => {
      const stream = streamRef.current;
      if (stream) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* hardware races */ }
        streamRef.current = null;
      }
      const video = videoRef?.current;
      if (video) {
        try { video.srcObject = null; } catch { /* DOM races */ }
      }
    };

    const onVisibility = () => { if (document.hidden) stop(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', stop);
    window.addEventListener('beforeunload', stop);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('beforeunload', stop);
      // Final stop on unmount — idempotent with any in-component
      // stopCamera the caller already runs. Belt + braces.
      stop();
    };
    // We deliberately depend on the refs themselves, NOT their
    // .current values, so the effect runs once per mount.
  }, [streamRef, videoRef]);
}

/**
 * useBlobUrlLifecycle — auto-revoke a single object URL on either
 * value change or unmount. Used in flows like BoothVideoMessage
 * where a recorded blob is held as an object URL for review
 * playback; before this hook, the URL leaked the moment the guest
 * preserved the message and the kiosk moved on.
 *
 * Usage:
 *   const [blobUrl, setBlobUrl] = useState(null);
 *   useBlobUrlLifecycle(blobUrl);
 *
 * @param {string|null} url
 */
export function useBlobUrlLifecycle(url) {
  useEffect(() => {
    if (!url) return undefined;
    return () => {
      try { URL.revokeObjectURL(url); } catch { /* already revoked */ }
    };
  }, [url]);
}
